import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, fmtEUR } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeftRight, Paperclip, Wallet, Lock } from "lucide-react";
import { useLists } from "@/hooks/useLists";
import { useAuth } from "@/context/AuthContext";

const DEFAULT_IN = ["bar", "caffetteria", "brunch", "aperitivi", "cucina", "eventi", "altre entrate"];
const DEFAULT_OUT = ["materie prime", "bevande", "personale", "affitto", "energia", "acqua", "internet", "assicurazioni", "commercialista", "software", "manutenzione", "attrezzature", "marketing", "imposte", "fornitori", "pulizie", "materiali di consumo", "altri costi"];
const ACCOUNT_TYPES = ["banca", "cassa", "carta", "altro"];

// Mirror of backend services/tx_filters.py::is_operating_tx — must stay in sync.
const isOperatingTx = (t) => {
  if (!t) return false;
  if (t.is_opening === true) return false;
  if (t.exclude_from_operating_metrics === true) return false;
  const cat = (t.category || "").toLowerCase();
  if (cat === "opening_balance") return false;
  if (cat === "trasferimento") return false;
  if (t.transfer_role === "in" || t.transfer_role === "out") return false;
  return true;
};

export default function Finance() {
  const { lists } = useLists();
  // FASE 7.4 STEP E.1 — action-button permission guards.
  const { can } = useAuth();
  const canAccountWrite = can("accounts.write");
  const canOpeningBalance = can("accounts.opening_balance");
  const canAccountDelete = can("accounts.delete");
  const canTxWrite = can("transactions.write");
  const canTxDelete = can("transactions.delete");
  const canTransfer = can("transfers.execute");
  const IN_CAT = lists.income_categories?.length ? lists.income_categories : DEFAULT_IN;
  const OUT_CAT = lists.expense_categories?.length ? lists.expense_categories : DEFAULT_OUT;
  const TYPES = lists.account_types?.length ? lists.account_types.filter((t) => t !== "POS") : ACCOUNT_TYPES;
  const [accounts, setAccounts] = useState([]);
  const [txs, setTxs] = useState([]);
  const [docCounts, setDocCounts] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [transferForm, setTransferForm] = useState({ from_account_id: "", to_account_id: "", amount: "", date: new Date().toISOString().slice(0, 10), description: "" });
  const [accountForm, setAccountForm] = useState({ name: "", type: "banca", opening_balance: "", opening_balance_date: new Date().toISOString().slice(0, 10), active: true, notes: "" });
  const [form, setForm] = useState({ type: "in", amount: "", account_id: "", category: "", subcategory: "", description: "", date: new Date().toISOString().slice(0, 10), vat: 22 });

  const load = async () => {
    const [a, t, dc] = await Promise.all([api.get("/accounts"), api.get("/transactions"), api.get("/documents/counts-by-transaction")]);
    setAccounts(a.data); setTxs(t.data); setDocCounts(dc.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/transactions", { ...form, amount: parseFloat(form.amount) });
      toast.success("Movimento registrato");
      setDialogOpen(false);
      setForm({ ...form, amount: "", description: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const submitAccount = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: accountForm.name.trim(),
        type: accountForm.type,
        active: accountForm.active,
        notes: accountForm.notes?.trim() || null,
      };
      const ob = parseFloat(accountForm.opening_balance);
      if (!Number.isNaN(ob)) {
        payload.opening_balance = ob;
        if (ob !== 0) payload.opening_balance_date = accountForm.opening_balance_date;
      }
      if (editAccount) {
        await api.put(`/accounts/${editAccount.id}`, {
          name: payload.name, type: payload.type, active: payload.active, notes: payload.notes,
        });
        toast.success("Conto aggiornato");
      } else {
        await api.post("/accounts", payload);
        toast.success("Conto creato" + (payload.opening_balance ? ` (opening: ${fmtEUR(payload.opening_balance)})` : ""));
      }
      setAccountOpen(false); setEditAccount(null);
      setAccountForm({ name: "", type: "banca", opening_balance: "", opening_balance_date: new Date().toISOString().slice(0, 10), active: true, notes: "" });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const openEdit = (a) => {
    setEditAccount(a);
    setAccountForm({ name: a.name, type: a.type, opening_balance: "", opening_balance_date: a.opening_balance_date || "", active: a.active !== false, notes: a.notes || "" });
    setAccountOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare il movimento?")) return;
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Eliminato");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossibile eliminare");
    }
  };

  // Operating totals — EXCLUDE opening balances + transfers.
  const opTxs = txs.filter(isOperatingTx);
  const totalIn = opTxs.filter((t) => t.type === "in").reduce((s, t) => s + Number(t.amount), 0);
  const totalOut = opTxs.filter((t) => t.type === "out").reduce((s, t) => s + Number(t.amount), 0);
  const totalLiquidity = accounts.reduce((s, a) => s + Number(a.balance || 0), 0);

  return (
    <div data-testid="finance-page">
      <PageHeader eyebrow="Finanze" title="Conti & movimenti"
        subtitle="Gestione multi-conto con entrate, uscite e categorie."
        actions={
          <div className="flex gap-2 flex-wrap">
            {canAccountWrite && (
            <Dialog open={accountOpen} onOpenChange={(v) => { setAccountOpen(v); if (!v) setEditAccount(null); }}>
              <DialogTrigger asChild>
                <Button data-testid="new-account-btn" variant="outline" onClick={() => setEditAccount(null)}>
                  <Wallet size={14} strokeWidth={1.5} /> Nuovo conto
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-serif">{editAccount ? "Modifica conto" : "Nuovo conto"}</DialogTitle></DialogHeader>
                <form onSubmit={submitAccount} className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Nome</Label>
                      <Input data-testid="acc-name" required value={accountForm.name} onChange={(e) => setAccountForm({ ...accountForm, name: e.target.value })} placeholder="Es. Banco di Sardegna" />
                    </div>
                    <div>
                      <Label>Tipo</Label>
                      <Select value={accountForm.type} onValueChange={(v) => setAccountForm({ ...accountForm, type: v })}>
                        <SelectTrigger data-testid="acc-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  {!editAccount && canOpeningBalance && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Saldo iniziale (€)</Label>
                        <Input data-testid="acc-opening" type="number" step="0.01" value={accountForm.opening_balance} onChange={(e) => setAccountForm({ ...accountForm, opening_balance: e.target.value })} placeholder="0.00" />
                        <p className="text-xs text-neutral-500 mt-1">Escluso da entrate/uscite operative</p>
                      </div>
                      <div>
                        <Label>Data saldo iniziale</Label>
                        <Input data-testid="acc-opening-date" type="date" value={accountForm.opening_balance_date} onChange={(e) => setAccountForm({ ...accountForm, opening_balance_date: e.target.value })} />
                        <p className="text-xs text-neutral-500 mt-1">Cutover: import dal giorno successivo</p>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex items-center gap-2 mt-6">
                      <input id="acc-active" type="checkbox" checked={accountForm.active} onChange={(e) => setAccountForm({ ...accountForm, active: e.target.checked })} data-testid="acc-active" />
                      <Label htmlFor="acc-active">Attivo</Label>
                    </div>
                  </div>
                  <div>
                    <Label>Note</Label>
                    <Textarea data-testid="acc-notes" rows={2} value={accountForm.notes} onChange={(e) => setAccountForm({ ...accountForm, notes: e.target.value })} placeholder="Opzionale · IBAN, filiale, contatto…" />
                  </div>
                  {editAccount && (
                    <div className="text-xs text-neutral-500 bg-[#F3EDE1] rounded p-2 flex gap-2 items-start">
                      <Lock size={12} className="mt-0.5" />
                      <span>Saldo iniziale e data sono immutabili dopo la creazione (elimina e ricrea il conto per modificarli).</span>
                    </div>
                  )}
                  <DialogFooter>
                    <Button data-testid="acc-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">{editAccount ? "Salva modifiche" : "Crea conto"}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            )}
            {canTransfer && (
            <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
              <DialogTrigger asChild>
                <Button data-testid="transfer-btn" variant="outline">
                  <ArrowLeftRight size={14} strokeWidth={1.5} /> Trasferimento
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-serif">Trasferimento tra conti</DialogTitle></DialogHeader>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (transferForm.from_account_id === transferForm.to_account_id) { toast.error("Scegli due conti diversi"); return; }
                  try {
                    await api.post("/accounts/transfer", { ...transferForm, amount: parseFloat(transferForm.amount) });
                    toast.success("Trasferimento eseguito");
                    setTransferOpen(false);
                    setTransferForm({ from_account_id: "", to_account_id: "", amount: "", date: new Date().toISOString().slice(0, 10), description: "" });
                    load();
                  } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
                }} className="space-y-4">
                  <div>
                    <Label>Da (conto sorgente)</Label>
                    <Select value={transferForm.from_account_id} onValueChange={(v) => setTransferForm({ ...transferForm, from_account_id: v })}>
                      <SelectTrigger data-testid="transfer-from"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                      <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {fmtEUR(a.balance)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>A (conto destinazione)</Label>
                    <Select value={transferForm.to_account_id} onValueChange={(v) => setTransferForm({ ...transferForm, to_account_id: v })}>
                      <SelectTrigger data-testid="transfer-to"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                      <SelectContent>{accounts.filter((a) => a.id !== transferForm.from_account_id).map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {fmtEUR(a.balance)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Importo (€)</Label><Input data-testid="transfer-amount" type="number" step="0.01" required value={transferForm.amount} onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })} /></div>
                    <div><Label>Data</Label><Input type="date" value={transferForm.date} onChange={(e) => setTransferForm({ ...transferForm, date: e.target.value })} /></div>
                  </div>
                  <div><Label>Descrizione</Label><Input value={transferForm.description} onChange={(e) => setTransferForm({ ...transferForm, description: e.target.value })} placeholder="Es. Versamento cassa in banca" /></div>
                  <DialogFooter><Button data-testid="transfer-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">Esegui trasferimento</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            )}
            {canTxWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-transaction-btn" className="bg-neutral-900 hover:bg-neutral-800 text-[#FDFBF7]">
                <Plus size={14} strokeWidth={1.5} /> Nuovo movimento
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-serif">Nuovo movimento</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Tipo</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, category: "" })}>
                      <SelectTrigger data-testid="tx-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in">Entrata</SelectItem>
                        <SelectItem value="out">Uscita</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Importo (€)</Label>
                    <Input data-testid="tx-amount" type="number" step="0.01" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label>Conto</Label>
                  <Select value={form.account_id} onValueChange={(v) => setForm({ ...form, account_id: v })}>
                    <SelectTrigger data-testid="tx-account"><SelectValue placeholder="Seleziona conto" /></SelectTrigger>
                    <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Categoria</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="tx-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
                    <SelectContent>{(form.type === "in" ? IN_CAT : OUT_CAT).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                  <div><Label>IVA %</Label><Input type="number" value={form.vat} onChange={(e) => setForm({ ...form, vat: parseInt(e.target.value) })} /></div>
                </div>
                <div><Label>Descrizione</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <DialogFooter>
                  <Button data-testid="tx-submit" type="submit" className="bg-neutral-900 text-[#FDFBF7]">Salva</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          )}
          </div>
        } />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard label="Liquidità totale" value={fmtEUR(totalLiquidity)} hint={`${accounts.length} conti`} />
        <StatCard label="Entrate totali (storico)" value={fmtEUR(totalIn)} tone="good" />
        <StatCard label="Uscite totali (storico)" value={fmtEUR(totalOut)} tone="danger" />
      </div>

      <h2 className="font-serif text-2xl mb-3">Conti</h2>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-10">
        {accounts.length === 0 && (
          <div className="col-span-full text-sm text-neutral-500 border border-dashed border-[#E2D9C8] rounded-lg p-8 text-center">
            Nessun conto configurato. Clicca <b>Nuovo conto</b> per iniziare.
          </div>
        )}
        {accounts.map((a) => (
          <button key={a.id} type="button" onClick={() => canAccountWrite && openEdit(a)} disabled={!canAccountWrite} className={`text-left border border-[#E2D9C8] rounded-lg p-5 bg-white transition-colors ${canAccountWrite ? "hover:border-neutral-400 cursor-pointer" : "cursor-default"}`} data-testid={`account-${a.id}`}>
            <div className="flex items-center justify-between">
              <div className="label-eyebrow">{a.type}</div>
              {a.active === false && <span className="text-[10px] px-1.5 py-0.5 bg-neutral-200 rounded-full">inattivo</span>}
            </div>
            <div className="font-serif text-xl mt-1">{a.name}</div>
            <div className="font-serif text-3xl mt-3">{fmtEUR(a.balance)}</div>
            {a.opening_balance_date && (
              <div className="text-[11px] text-neutral-500 mt-2">
                Apertura {fmtEUR(a.opening_balance || 0)} al {a.opening_balance_date}
              </div>
            )}
            {a.notes && <div className="text-[11px] text-neutral-500 mt-1 truncate">{a.notes}</div>}
          </button>
        ))}
      </div>

      <h2 className="font-serif text-2xl mb-3">Movimenti recenti</h2>
      <div className="border border-[#E2D9C8] rounded-lg bg-white overflow-x-auto">
        <table className="w-full text-sm zebra min-w-[720px]">
          <thead>
            <tr className="text-left text-neutral-500 label-eyebrow">
              <th className="p-3">Data</th><th className="p-3">Tipo</th><th className="p-3">Categoria</th>
              <th className="p-3">Descrizione</th><th className="p-3 text-right">Importo</th>
              <th className="p-3 text-center">Doc.</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {txs.slice(0, 100).map((t) => {
              const dc = docCounts[t.id] || 0;
              const isTech = !isOperatingTx(t);
              return (
              <tr key={t.id} className={`border-t border-[#E2D9C8] ${isTech ? "bg-neutral-50" : ""}`} data-testid={`tx-row-${t.id}`}>
                <td className="p-3">{t.date}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs ${t.type === "in" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{t.type === "in" ? "Entrata" : "Uscita"}</span>
                  {t.is_opening && <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-800 rounded-full" title="Movimento tecnico di apertura — escluso da metriche operative">apertura</span>}
                  {(t.category || "").toLowerCase() === "trasferimento" && <span className="ml-1 text-[10px] px-1.5 py-0.5 bg-sky-100 text-sky-800 rounded-full">giroconto</span>}
                </td>
                <td className="p-3 capitalize">{t.category}</td>
                <td className="p-3 text-neutral-600">{t.description}</td>
                <td className="p-3 text-right font-medium">{fmtEUR(t.amount)}</td>
                <td className="p-3 text-center">
                  {dc > 0 ? (
                    <a href="/documents" className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#F3EDE1] rounded-full hover:bg-[#E2D9C8]" data-testid={`docs-count-${t.id}`}>
                      <Paperclip size={10} strokeWidth={1.5} /> {dc}
                    </a>
                  ) : <span className="text-neutral-300 text-xs">—</span>}
                </td>
                <td className="p-3 text-right">
                  {t.is_opening
                    ? <span className="text-neutral-300" title="Il movimento di apertura non può essere eliminato singolarmente"><Lock size={14} strokeWidth={1.5} /></span>
                    : canTxDelete
                      ? <button onClick={() => del(t.id)} className="text-neutral-400 hover:text-red-700"><Trash2 size={14} strokeWidth={1.5} /></button>
                      : <span className="text-neutral-200"><Lock size={14} strokeWidth={1.5} /></span>}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
